import React, { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  ChevronDown,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { companyInfo } from "../data/mockData";
import api from "../lib/api";

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const FaqItem = ({ item, isOpen, toggle }) => {
  return (
    <div className="border border-[#E8DDD3]/50 rounded-xl overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-[#FAF9F6] transition-colors"
      >
        <span className="font-semibold text-[#1A1A2E] text-sm pr-4">
          {item.question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-[#C4956A] flex-shrink-0 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-5 pb-5">
          <p className="text-sm text-[#1A1A2E]/60 leading-relaxed">
            {item.answer}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [faqItems, setFaqItems] = useState([]);

  useEffect(() => {
    const fetchFaq = async () => {
      try {
        const res = await api.get("/faq");
        setFaqItems(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching FAQ:", err);
      }
    };
    fetchFaq();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", formData);
      setSubmitted(true);
      setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      }, 2000);
    } catch (err) {
      console.error("Error submitting contact:", err);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#FAF9F6] relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#C4956A]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
              Contact
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#1A1A2E] mt-4">
              Parlons de{" "}
              <span className="text-[#C4956A]">votre projet</span>
            </h1>
            <p className="text-lg text-[#1A1A2E]/60 mt-6 leading-relaxed">
              {"Contactez-nous pour un premier échange gratuit et sans engagement. Nous sommes là pour répondre à toutes vos questions."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <AnimatedSection>
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-[#1A1A2E]">
                    {"Nos coordonnées"}
                  </h2>
                  <div className="space-y-4">
                    <a
                      href={`tel:${companyInfo.phone}`}
                      className="flex items-center gap-4 p-4 bg-[#FAF9F6] rounded-xl hover:bg-[#C4956A]/5 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A] transition-colors">
                        <Phone className="w-5 h-5 text-[#C4956A] group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-xs text-[#1A1A2E]/40 uppercase tracking-wider">
                          {"Téléphone"}
                        </p>
                        <p className="text-sm font-semibold text-[#1A1A2E]">
                          {companyInfo.phone}
                        </p>
                      </div>
                    </a>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="flex items-center gap-4 p-4 bg-[#FAF9F6] rounded-xl hover:bg-[#C4956A]/5 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A] transition-colors">
                        <Mail className="w-5 h-5 text-[#C4956A] group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-xs text-[#1A1A2E]/40 uppercase tracking-wider">
                          Email
                        </p>
                        <p className="text-sm font-semibold text-[#1A1A2E]">
                          {companyInfo.email}
                        </p>
                      </div>
                    </a>
                    <div className="flex items-center gap-4 p-4 bg-[#FAF9F6] rounded-xl">
                      <div className="w-12 h-12 rounded-xl bg-[#C4956A]/10 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-[#C4956A]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#1A1A2E]/40 uppercase tracking-wider">
                          Localisation
                        </p>
                        <p className="text-sm font-semibold text-[#1A1A2E]">
                          {companyInfo.address}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-[#FAF9F6] rounded-xl">
                      <div className="w-12 h-12 rounded-xl bg-[#C4956A]/10 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-[#C4956A]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#1A1A2E]/40 uppercase tracking-wider">
                          {"Disponibilité"}
                        </p>
                        <p className="text-sm font-semibold text-[#1A1A2E]">
                          Lun - Ven : 9h - 18h
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <AnimatedSection>
                <div className="bg-[#FAF9F6] rounded-2xl p-8 lg:p-10">
                  {submitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-16 h-16 rounded-full bg-[#C4956A]/10 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-[#C4956A]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#1A1A2E]">
                        {"Message envoyé !"}
                      </h3>
                      <p className="text-[#1A1A2E]/60 mt-2">
                        {"Nous vous recontacterons dans les plus brefs délais."}
                      </p>
                      <button
                        onClick={() => setSubmitted(false)}
                        className="mt-6 px-6 py-2 bg-[#C4956A] text-white text-sm font-medium rounded-lg hover:bg-[#B38459] transition-colors"
                      >
                        Envoyer un autre message
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <h2 className="text-2xl font-bold text-[#1A1A2E] mb-6">
                        Envoyez-nous un message
                      </h2>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                            {"Prénom *"}
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors"
                            placeholder={"Votre prénom"}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                            Nom *
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors"
                            placeholder="Votre nom"
                          />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                            Email *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors"
                            placeholder="votre@email.com"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                            {"Téléphone"}
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors"
                            placeholder="06 XX XX XX XX"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                          Sujet
                        </label>
                        <select
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors appearance-none"
                        >
                          <option value="">{"Sélectionnez un sujet"}</option>
                          <option value="projet">{"Nouveau projet d'investissement"}</option>
                          <option value="info">{"Demande d'informations"}</option>
                          <option value="parrainage">Programme de parrainage</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                          Message *
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors resize-none"
                          placeholder={"Décrivez votre projet..."}
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all duration-300 hover:shadow-lg hover:shadow-[#C4956A]/20"
                      >
                        Envoyer le message
                        <Send className="w-4 h-4" />
                      </button>
                    </form>
                  )}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
                FAQ
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E] mt-3">
                {"Questions fréquentes"}
              </h2>
            </div>
          </AnimatedSection>

          <div className="space-y-3">
            {Array.isArray(faqItems) ? faqItems.map((item, i) => (
              <AnimatedSection key={i}>
                <FaqItem
                  item={item}
                  isOpen={openFaq === i}
                  toggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              </AnimatedSection>
            )) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
